import { apiClient, type MemberRole } from "@nabble/sdk";
import type { UserStatus } from "../model/userStatus";

export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  status: UserStatus;
  mfaEnrolled: boolean;
  /** Suite administrators manage the whole installation from the Admin Center. */
  suiteAdmin: boolean;
  /** The organizations the person belongs to, with every role they hold in each. */
  memberships: { organizationId: string; roles: MemberRole[] }[];
  lastLoginAt: string | null;
  self: boolean;
}

/** When the email couldn't be sent, the single-use link to share another way. */
export interface EmailedLink {
  emailSent: boolean;
  link: string | null;
}

export interface CreateUserResult extends EmailedLink {
  id: string;
}

export interface NewUser {
  email: string;
  displayName: string;
  organizationId: string;
  /** The roles they hold in that organization (at least one). */
  roleIds: string[];
  suiteAdmin: boolean;
}

export function fetchUsers(): Promise<AdminUser[]> {
  return apiClient<AdminUser[]>("/api/admin/users");
}

export function createUser(body: NewUser): Promise<CreateUserResult> {
  return apiClient<CreateUserResult>("/api/admin/users", { method: "POST", body });
}

/** Whether the person is a suite administrator; roles are held per organization. */
export function updateUserAccess(id: string, suiteAdmin: boolean): Promise<void> {
  return apiClient<void>(`/api/admin/users/${id}/roles`, { method: "PUT", body: { suiteAdmin } });
}

/** Adds the person to an organization with these roles, or replaces their roles there. */
export function setUserMembership(id: string, organizationId: string, roleIds: string[]): Promise<void> {
  return apiClient<void>(`/api/admin/users/${id}/memberships/${organizationId}`, { method: "PUT", body: { roleIds } });
}

export function removeUserMembership(id: string, organizationId: string): Promise<void> {
  return apiClient<void>(`/api/admin/users/${id}/memberships/${organizationId}`, { method: "DELETE" });
}

export function updateUserStatus(id: string, status: UserStatus): Promise<void> {
  return apiClient<void>(`/api/admin/users/${id}/status`, { method: "POST", body: { status } });
}

export function resetUserPassword(id: string): Promise<EmailedLink> {
  return apiClient<EmailedLink>(`/api/admin/users/${id}/password-reset`, { method: "POST" });
}

export function resetUserMfa(id: string): Promise<void> {
  return apiClient<void>(`/api/admin/users/${id}/mfa-reset`, { method: "POST" });
}

export function anonymizeUser(id: string): Promise<void> {
  return apiClient<void>(`/api/admin/users/${id}/anonymize`, { method: "POST" });
}
